import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  UploadCloud,
  ShieldCheck,
  ShieldAlert,
  FileWarning,
  FileCheck2,
  FileX2,
  Loader2,
  RefreshCw,
  Download,
  Search,
  X,
  SlidersHorizontal,
  ChevronDown,
  ChevronUp,
  RotateCcw,
  UserRound,
  Clock3,
  Fingerprint,
  FileKey2,
  CheckCircle2,
  XCircle,
  AlertTriangle
} from 'lucide-react';

const API =
  process.env.REACT_APP_API_URL ||
  'https://api-pak.sudindikjt2.site';

const statusMeta = {
  valid: {
    label: 'TTE Valid',
    badge: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    icon: CheckCircle2
  },
  valid_warning: {
    label: 'Valid • Cek Sertifikat',
    badge: 'bg-amber-50 text-amber-700 border-amber-200',
    icon: AlertTriangle
  },
  invalid: {
    label: 'Tidak Valid',
    badge: 'bg-rose-50 text-rose-700 border-rose-200',
    icon: XCircle
  },
  no_signature: {
    label: 'Belum TTE',
    badge: 'bg-slate-100 text-slate-700 border-slate-200',
    icon: FileWarning
  },
  error: {
    label: 'Gagal Verifikasi',
    badge: 'bg-rose-50 text-rose-700 border-rose-200',
    icon: XCircle
  },
  server_error: {
    label: 'Server Belum Siap',
    badge: 'bg-rose-50 text-rose-700 border-rose-200',
    icon: XCircle
  }
};

const fmtSize = (bytes) => {
  if (!bytes) return '-';

  const units = ['B', 'KB', 'MB', 'GB'];
  let n = Number(bytes);
  let i = 0;

  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i++;
  }

  return `${n.toFixed(i >= 2 ? 2 : 1)} ${units[i]}`;
};

const normalize = (value) =>
  String(value ?? '')
    .toLowerCase()
    .trim();

const csvEscape = (value) =>
  `"${String(value ?? '').replace(/"/g, '""')}"`;

const SummaryCard = ({
  label,
  value,
  type,
  active,
  onClick
}) => {
  const cfg = {
    all: {
      wrap: 'bg-white border-slate-200',
      icon: FileCheck2
    },
    valid: {
      wrap: 'bg-emerald-50 border-emerald-200',
      icon: ShieldCheck
    },
    warning: {
      wrap: 'bg-amber-50 border-amber-200',
      icon: ShieldAlert
    },
    invalid: {
      wrap: 'bg-rose-50 border-rose-200',
      icon: FileX2
    },
    unsigned: {
      wrap: 'bg-slate-100 border-slate-200',
      icon: FileWarning
    }
  }[type];

  const Icon = cfg.icon;

  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        'text-left border rounded-2xl p-4 transition-all',
        cfg.wrap,
        active
          ? 'ring-2 ring-blue-500 ring-offset-1'
          : 'hover:-translate-y-0.5 hover:shadow-sm'
      ].join(' ')}
    >
      <Icon size={20} className="mb-3 text-slate-700" />
      <div className="text-2xl font-bold">{value}</div>
      <div className="text-xs text-slate-500 mt-1">{label}</div>
    </button>
  );
};

export default function TteVerificationPage() {
  const inputRef = useRef(null);

  const [files, setFiles] = useState([]);
  const [dragging, setDragging] = useState(false);

  const [health, setHealth] = useState(null);
  const [checkingHealth, setCheckingHealth] = useState(true);

  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  // Filter
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [signerFilter, setSignerFilter] = useState('all');
  const [trustFilter, setTrustFilter] = useState('all');
  const [sortBy, setSortBy] = useState('file_asc');

  // Expanded detail
  const [expandedRow, setExpandedRow] = useState(null);

  const token = localStorage.getItem('token');

  const checkHealth = async () => {
    setCheckingHealth(true);
    setError('');

    try {
      const response = await fetch(
        `${API}/api/tte-verify/health`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.msg || 'Akses verifikasi TTE ditolak.'
        );
      }

      setHealth(data);
    } catch (err) {
      setHealth(null);
      setError(err.message);
    } finally {
      setCheckingHealth(false);
    }
  };

  useEffect(() => {
    checkHealth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addFiles = (fileList) => {
    const incoming = Array.from(fileList || [])
      .filter(file => /\.(pdf|zip)$/i.test(file.name));

    setFiles(prev => {
      const map = new Map();

      [...prev, ...incoming].forEach(file => {
        const key =
          `${file.name}-${file.size}-${file.lastModified}`;

        map.set(key, file);
      });

      return [...map.values()];
    });

    setResult(null);
    setError('');
    setExpandedRow(null);
  };

  const totalUploadSize = useMemo(
    () =>
      files.reduce(
        (sum, file) => sum + Number(file.size || 0),
        0
      ),
    [files]
  );

  const verify = async () => {
    if (!files.length || loading) return;

    setLoading(true);
    setError('');
    setResult(null);
    setExpandedRow(null);

    try {
      const form = new FormData();

      files.forEach(file => {
        form.append('files', file);
      });

      const response = await fetch(
        `${API}/api/tte-verify/verify`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`
          },
          body: form
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.msg || 'Verifikasi gagal.'
        );
      }

      setResult(data);
      setStatusFilter('all');
      setSignerFilter('all');
      setTrustFilter('all');
      setSearch('');
      setSortBy('file_asc');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const signerOptions = useMemo(() => {
    if (!result?.results?.length) return [];

    const names = new Set();

    result.results.forEach(item => {
      (item.signatures || []).forEach(sig => {
        if (sig.signer_common_name) {
          names.add(sig.signer_common_name);
        }
      });
    });

    return [...names].sort((a, b) =>
      a.localeCompare(b, 'id')
    );
  }, [result]);

  const filteredResults = useMemo(() => {
    if (!result?.results?.length) return [];

    const q = normalize(search);

    const rows = result.results.filter(item => {
      const signatures = item.signatures || [];

      const signerText = signatures
        .map(sig =>
          [
            sig.signer_common_name,
            sig.signer_dn,
            sig.field_name,
            sig.signing_time,
            sig.hash_algorithm,
            sig.signature_type,
            sig.validation,
            sig.certificate_validation
          ].join(' ')
        )
        .join(' ');

      const searchable = normalize(
        [
          item.file_name,
          item.status_label,
          item.message,
          signerText
        ].join(' ')
      );

      if (q && !searchable.includes(q)) {
        return false;
      }

      if (
        statusFilter !== 'all' &&
        item.status !== statusFilter
      ) {
        return false;
      }

      if (signerFilter !== 'all') {
        const matchSigner = signatures.some(
          sig =>
            sig.signer_common_name === signerFilter
        );

        if (!matchSigner) return false;
      }

      if (trustFilter === 'trusted') {
        if (!item.certificate_trusted) return false;
      }

      if (trustFilter === 'untrusted') {
        if (item.certificate_trusted) return false;
      }

      return true;
    });

    const sorted = [...rows];

    sorted.sort((a, b) => {
      if (sortBy === 'file_asc') {
        return String(a.file_name).localeCompare(
          String(b.file_name),
          'id'
        );
      }

      if (sortBy === 'file_desc') {
        return String(b.file_name).localeCompare(
          String(a.file_name),
          'id'
        );
      }

      if (sortBy === 'status') {
        const order = {
          invalid: 1,
          no_signature: 2,
          error: 3,
          server_error: 4,
          valid_warning: 5,
          valid: 6
        };

        return (
          (order[a.status] || 99) -
          (order[b.status] || 99)
        );
      }

      if (sortBy === 'signer') {
        const aa =
          a.signatures?.[0]?.signer_common_name || '';
        const bb =
          b.signatures?.[0]?.signer_common_name || '';

        return aa.localeCompare(bb, 'id');
      }

      return 0;
    });

    return sorted;
  }, [
    result,
    search,
    statusFilter,
    signerFilter,
    trustFilter,
    sortBy
  ]);

  const activeFilterCount = useMemo(() => {
    let n = 0;

    if (search.trim()) n++;
    if (statusFilter !== 'all') n++;
    if (signerFilter !== 'all') n++;
    if (trustFilter !== 'all') n++;
    if (sortBy !== 'file_asc') n++;

    return n;
  }, [
    search,
    statusFilter,
    signerFilter,
    trustFilter,
    sortBy
  ]);

  const resetFilters = () => {
    setSearch('');
    setStatusFilter('all');
    setSignerFilter('all');
    setTrustFilter('all');
    setSortBy('file_asc');
    setExpandedRow(null);
  };

  const exportCsv = () => {
    if (!filteredResults.length) return;

    const header = [
      'Nama File',
      'Ukuran',
      'Status',
      'Pesan',
      'Jumlah TTE',
      'Penandatangan',
      'Distinguished Name',
      'Field Signature',
      'Waktu TTE',
      'Hash',
      'Tipe Signature',
      'Validasi Signature',
      'Seluruh PDF Ditandatangani',
      'Sertifikat Dipercaya Server',
      'Validasi Sertifikat'
    ];

    const rows = [
      header,
      ...filteredResults.map(item => {
        const sig = item.signatures?.[0] || {};

        return [
          item.file_name,
          fmtSize(item.size_bytes),
          item.status_label,
          item.message,
          item.signature_count,
          sig.signer_common_name || '',
          sig.signer_dn || '',
          sig.field_name || '',
          sig.signing_time || '',
          sig.hash_algorithm || '',
          sig.signature_type || '',
          sig.validation || '',
          item.whole_document_signed ? 'Ya' : 'Tidak',
          item.certificate_trusted ? 'Ya' : 'Belum',
          sig.certificate_validation || ''
        ];
      })
    ];

    const csv =
      rows
        .map(row =>
          row.map(csvEscape).join(',')
        )
        .join('\n');

    const blob = new Blob(
      ['\uFEFF' + csv],
      {
        type: 'text/csv;charset=utf-8'
      }
    );

    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');

    anchor.href = url;
    anchor.download =
      `verifikasi_tte_${Date.now()}.csv`;

    anchor.click();
    URL.revokeObjectURL(url);
  };

  if (checkingHealth) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center text-slate-600">
          <Loader2
            className="animate-spin mx-auto mb-3"
            size={34}
          />
          Memeriksa layanan verifikasi TTE...
        </div>
      </div>
    );
  }

  if (!health) {
    return (
      <div className="min-h-screen bg-slate-50 p-6 flex items-center justify-center">
        <div className="max-w-lg w-full bg-white border border-rose-200 rounded-2xl p-6 shadow-sm">
          <ShieldAlert
            className="text-rose-600 mb-3"
            size={32}
          />

          <h1 className="text-xl font-bold text-slate-900">
            Akses Tidak Tersedia
          </h1>

          <p className="text-sm text-slate-600 mt-2">
            {error}
          </p>

          <button
            type="button"
            onClick={checkHealth}
            className="mt-5 px-4 py-2 rounded-xl bg-slate-900 text-white font-semibold inline-flex items-center gap-2"
          >
            <RefreshCw size={16} />
            Coba Lagi
          </button>
        </div>
      </div>
    );
  }

  const summary = result?.summary;

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <div className="max-w-[1600px] mx-auto px-4 md:px-8 py-7">
        {/* HEADER */}
        <div className="flex flex-col md:flex-row md:items-start justify-between gap-4 mb-6">
          <div>
            <div className="text-xs font-bold tracking-[0.16em] text-blue-600 uppercase">
              SIPAK · TTE
            </div>

            <h1 className="text-2xl md:text-3xl font-bold mt-1">
              Verifikasi Dokumen TTE
            </h1>

            <p className="text-sm text-slate-600 mt-2 max-w-3xl">
              Periksa PDF hasil BeSign secara massal.
              Admin dan Satlak dapat mencari dokumen,
              menyaring yang bermasalah, dan melihat
              detail teknis tiap tanda tangan.
            </p>
          </div>

          <div className="bg-white rounded-xl border px-4 py-3 text-xs text-slate-600">
            <div className="font-bold text-slate-800">
              Akses
            </div>

            <div className="mt-1 capitalize">
              {health.role}
            </div>

            <div className="mt-1">
              Verifier:{' '}
              {health.pdfsig_installed
                ? 'Siap'
                : 'Belum terpasang'}
            </div>
          </div>
        </div>

        {!health.pdfsig_installed && (
          <div className="mb-5 border border-amber-200 bg-amber-50 text-amber-800 rounded-xl px-4 py-3 text-sm">
            Server belum memiliki <b>pdfsig</b>.
            Install <b>poppler-utils</b> terlebih dahulu.
          </div>
        )}

        {/* UPLOAD */}
        <div
          onDragOver={event => {
            event.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => {
            setDragging(false);
          }}
          onDrop={event => {
            event.preventDefault();
            setDragging(false);
            addFiles(event.dataTransfer.files);
          }}
          onClick={() =>
            inputRef.current?.click()
          }
          className={[
            'bg-white border-2 border-dashed rounded-2xl p-8 md:p-12 text-center cursor-pointer transition',
            dragging
              ? 'border-blue-500 bg-blue-50'
              : 'border-slate-300 hover:border-blue-400'
          ].join(' ')}
        >
          <input
            ref={inputRef}
            type="file"
            multiple
            accept=".pdf,.zip,application/pdf,application/zip"
            className="hidden"
            onChange={event =>
              addFiles(event.target.files)
            }
          />

          <UploadCloud
            className="mx-auto text-blue-600 mb-3"
            size={42}
          />

          <div className="font-bold text-lg">
            Pilih atau tarik PDF / ZIP ke sini
          </div>

          <div className="text-sm text-slate-500 mt-1">
            Bisa satu file, banyak PDF, atau ZIP hasil
            batch BeSign.
          </div>
        </div>

        {files.length > 0 && (
          <div className="bg-white border rounded-2xl mt-4 overflow-hidden">
            <div className="px-5 py-4 border-b flex flex-col sm:flex-row gap-3 justify-between sm:items-center">
              <div>
                <div className="font-bold">
                  {files.length} file dipilih
                </div>

                <div className="text-xs text-slate-500">
                  Total upload{' '}
                  {fmtSize(totalUploadSize)}
                </div>
              </div>

              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={event => {
                    event.stopPropagation();
                    setFiles([]);
                    setResult(null);
                    resetFilters();
                  }}
                  className="px-4 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-sm font-semibold"
                >
                  Bersihkan
                </button>

                <button
                  type="button"
                  disabled={
                    loading ||
                    !health.pdfsig_installed
                  }
                  onClick={event => {
                    event.stopPropagation();
                    verify();
                  }}
                  className="px-5 py-2 rounded-xl bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white text-sm font-bold inline-flex items-center gap-2"
                >
                  {loading ? (
                    <>
                      <Loader2
                        className="animate-spin"
                        size={17}
                      />
                      Memverifikasi...
                    </>
                  ) : (
                    <>
                      <ShieldCheck size={17} />
                      Verifikasi
                    </>
                  )}
                </button>
              </div>
            </div>

            <div className="max-h-48 overflow-auto px-5 py-3 text-sm">
              {files.map((file, index) => (
                <div
                  key={`${file.name}-${index}`}
                  className="flex justify-between gap-4 py-1.5 border-b last:border-0"
                >
                  <span className="truncate">
                    {file.name}
                  </span>

                  <span className="text-slate-400 shrink-0">
                    {fmtSize(file.size)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {error && (
          <div className="mt-5 border border-rose-200 bg-rose-50 text-rose-700 rounded-xl px-4 py-3 text-sm">
            {error}
          </div>
        )}

        {summary && (
          <>
            {/* KPI CLICKABLE */}
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mt-6">
              <SummaryCard
                label="Total"
                value={summary.total}
                type="all"
                active={statusFilter === 'all'}
                onClick={() =>
                  setStatusFilter('all')
                }
              />

              <SummaryCard
                label="Valid"
                value={summary.valid}
                type="valid"
                active={statusFilter === 'valid'}
                onClick={() =>
                  setStatusFilter('valid')
                }
              />

              <SummaryCard
                label="Valid + Warning"
                value={summary.valid_warning}
                type="warning"
                active={
                  statusFilter === 'valid_warning'
                }
                onClick={() =>
                  setStatusFilter('valid_warning')
                }
              />

              <SummaryCard
                label="Tidak Valid"
                value={summary.invalid}
                type="invalid"
                active={statusFilter === 'invalid'}
                onClick={() =>
                  setStatusFilter('invalid')
                }
              />

              <SummaryCard
                label="Belum TTE / Error"
                value={
                  summary.no_signature +
                  summary.error
                }
                type="unsigned"
                active={
                  statusFilter ===
                    'no_signature' ||
                  statusFilter === 'error' ||
                  statusFilter ===
                    'server_error'
                }
                onClick={() =>
                  setStatusFilter('no_signature')
                }
              />
            </div>

            {/* FILTER TOOLBAR */}
            <div className="mt-5 bg-white border rounded-2xl p-4">
              <div className="flex flex-col xl:flex-row gap-3">
                <div className="relative flex-1 min-w-[260px]">
                  <Search
                    size={17}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                  />

                  <input
                    value={search}
                    onChange={event =>
                      setSearch(event.target.value)
                    }
                    placeholder="Cari nama file, penandatangan, DN sertifikat, status..."
                    className="w-full h-11 pl-10 pr-10 border border-slate-300 rounded-xl outline-none focus:border-blue-500"
                  />

                  {!!search && (
                    <button
                      type="button"
                      onClick={() => setSearch('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>

                <select
                  value={statusFilter}
                  onChange={event =>
                    setStatusFilter(
                      event.target.value
                    )
                  }
                  className="h-11 border border-slate-300 rounded-xl px-3 bg-white"
                >
                  <option value="all">
                    Semua status
                  </option>
                  <option value="valid">
                    TTE Valid
                  </option>
                  <option value="valid_warning">
                    Valid + Warning
                  </option>
                  <option value="invalid">
                    Tidak Valid
                  </option>
                  <option value="no_signature">
                    Belum TTE
                  </option>
                  <option value="error">
                    Gagal Verifikasi
                  </option>
                  <option value="server_error">
                    Server Error
                  </option>
                </select>

                <select
                  value={signerFilter}
                  onChange={event =>
                    setSignerFilter(
                      event.target.value
                    )
                  }
                  className="h-11 border border-slate-300 rounded-xl px-3 bg-white"
                >
                  <option value="all">
                    Semua penandatangan
                  </option>

                  {signerOptions.map(name => (
                    <option
                      key={name}
                      value={name}
                    >
                      {name}
                    </option>
                  ))}
                </select>

                <select
                  value={trustFilter}
                  onChange={event =>
                    setTrustFilter(
                      event.target.value
                    )
                  }
                  className="h-11 border border-slate-300 rounded-xl px-3 bg-white"
                >
                  <option value="all">
                    Semua sertifikat
                  </option>
                  <option value="trusted">
                    Dipercaya server
                  </option>
                  <option value="untrusted">
                    Belum dipercaya server
                  </option>
                </select>

                <select
                  value={sortBy}
                  onChange={event =>
                    setSortBy(
                      event.target.value
                    )
                  }
                  className="h-11 border border-slate-300 rounded-xl px-3 bg-white"
                >
                  <option value="file_asc">
                    File A-Z
                  </option>
                  <option value="file_desc">
                    File Z-A
                  </option>
                  <option value="status">
                    Bermasalah dulu
                  </option>
                  <option value="signer">
                    Penandatangan
                  </option>
                </select>
              </div>

              <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
                <div className="text-sm text-slate-500 flex items-center gap-2">
                  <SlidersHorizontal size={15} />
                  Menampilkan{' '}
                  <b className="text-slate-800">
                    {filteredResults.length}
                  </b>{' '}
                  dari{' '}
                  <b className="text-slate-800">
                    {summary.total}
                  </b>{' '}
                  dokumen
                  {activeFilterCount > 0
                    ? ` • ${activeFilterCount} filter aktif`
                    : ''}
                </div>

                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setStatusFilter('invalid');
                      setTrustFilter('all');
                    }}
                    className="px-3 py-2 rounded-lg bg-rose-50 text-rose-700 hover:bg-rose-100 text-xs font-bold"
                  >
                    Hanya Tidak Valid
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setStatusFilter(
                        'valid_warning'
                      );
                    }}
                    className="px-3 py-2 rounded-lg bg-amber-50 text-amber-700 hover:bg-amber-100 text-xs font-bold"
                  >
                    Hanya Warning
                  </button>

                  <button
                    type="button"
                    disabled={
                      activeFilterCount === 0
                    }
                    onClick={resetFilters}
                    className="px-3 py-2 rounded-lg bg-slate-100 text-slate-700 hover:bg-slate-200 disabled:opacity-40 text-xs font-bold inline-flex items-center gap-1.5"
                  >
                    <RotateCcw size={14} />
                    Reset
                  </button>
                </div>
              </div>
            </div>

            {/* RESULT */}
            <div className="mt-4 bg-white border rounded-2xl overflow-hidden">
              <div className="px-5 py-4 border-b flex justify-between items-center gap-3">
                <div>
                  <div className="font-bold">
                    Hasil Verifikasi
                  </div>

                  <div className="text-xs text-slate-500 mt-1">
                    Klik <b>Detail</b> untuk melihat
                    signature field, DN penandatangan,
                    hash, tipe signature, dan hasil
                    validasi sertifikat.
                  </div>
                </div>

                <button
                  type="button"
                  disabled={!filteredResults.length}
                  onClick={exportCsv}
                  className="px-3 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 disabled:opacity-40 text-sm font-semibold inline-flex items-center gap-2"
                >
                  <Download size={15} />
                  CSV Hasil Filter
                </button>
              </div>

              {filteredResults.length === 0 ? (
                <div className="p-10 text-center text-slate-500">
                  Tidak ada dokumen yang sesuai filter.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm min-w-[1180px]">
                    <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                      <tr>
                        <th className="text-left px-4 py-3">
                          File
                        </th>
                        <th className="text-left px-4 py-3">
                          Status
                        </th>
                        <th className="text-left px-4 py-3">
                          Penandatangan
                        </th>
                        <th className="text-left px-4 py-3">
                          Waktu TTE
                        </th>
                        <th className="text-center px-4 py-3">
                          Integritas
                        </th>
                        <th className="text-center px-4 py-3">
                          Full PDF
                        </th>
                        <th className="text-left px-4 py-3">
                          Sertifikat
                        </th>
                        <th className="text-center px-4 py-3">
                          Detail
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      {filteredResults.map(
                        (item, index) => {
                          const signature =
                            item.signatures?.[0] || {};

                          const open =
                            expandedRow ===
                            `${item.file_name}-${index}`;

                          const rowKey =
                            `${item.file_name}-${index}`;

                          const meta =
                            statusMeta[
                              item.status
                            ] ||
                            statusMeta.error;

                          const StatusIcon =
                            meta.icon;

                          return (
                            <React.Fragment
                              key={rowKey}
                            >
                              <tr className="border-t align-top hover:bg-slate-50/60">
                                <td className="px-4 py-3">
                                  <div className="font-semibold max-w-[300px] break-all">
                                    {item.file_name}
                                  </div>

                                  <div className="text-xs text-slate-400 mt-1">
                                    {fmtSize(
                                      item.size_bytes
                                    )}
                                  </div>
                                </td>

                                <td className="px-4 py-3">
                                  <span
                                    className={[
                                      'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-bold',
                                      meta.badge
                                    ].join(' ')}
                                  >
                                    <StatusIcon
                                      size={13}
                                    />

                                    {item.status_label}
                                  </span>

                                  <div className="text-xs text-slate-500 mt-2 max-w-[290px] leading-relaxed">
                                    {item.message}
                                  </div>
                                </td>

                                <td className="px-4 py-3">
                                  <div className="font-semibold">
                                    {signature.signer_common_name ||
                                      '-'}
                                  </div>

                                  <div className="text-xs text-slate-400 mt-1">
                                    {item.signature_count ||
                                      0}{' '}
                                    tanda tangan
                                  </div>
                                </td>

                                <td className="px-4 py-3 text-xs whitespace-pre-line">
                                  {signature.signing_time ||
                                    '-'}
                                </td>

                                <td className="px-4 py-3 text-center">
                                  {item.cryptographic_valid ? (
                                    <CheckCircle2
                                      className="text-emerald-600 mx-auto"
                                      size={18}
                                    />
                                  ) : (
                                    <XCircle
                                      className="text-rose-600 mx-auto"
                                      size={18}
                                    />
                                  )}
                                </td>

                                <td className="px-4 py-3 text-center">
                                  {item.whole_document_signed ? (
                                    <CheckCircle2
                                      className="text-emerald-600 mx-auto"
                                      size={18}
                                    />
                                  ) : (
                                    <AlertTriangle
                                      className="text-amber-500 mx-auto"
                                      size={18}
                                    />
                                  )}
                                </td>

                                <td className="px-4 py-3">
                                  <div
                                    className={[
                                      'text-xs font-semibold',
                                      item.certificate_trusted
                                        ? 'text-emerald-700'
                                        : 'text-amber-700'
                                    ].join(' ')}
                                  >
                                    {item.certificate_trusted
                                      ? '✅ Dipercaya server'
                                      : '⚠️ Belum dipercaya server'}
                                  </div>

                                  <div className="text-[11px] text-slate-400 mt-1 max-w-[260px] break-words">
                                    {signature.certificate_validation ||
                                      '-'}
                                  </div>
                                </td>

                                <td className="px-4 py-3 text-center">
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setExpandedRow(
                                        open
                                          ? null
                                          : rowKey
                                      )
                                    }
                                    className="px-3 py-2 rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100 font-bold text-xs inline-flex items-center gap-1.5"
                                  >
                                    {open ? (
                                      <ChevronUp
                                        size={14}
                                      />
                                    ) : (
                                      <ChevronDown
                                        size={14}
                                      />
                                    )}

                                    Detail
                                  </button>
                                </td>
                              </tr>

                              {open && (
                                <tr className="bg-slate-50/80 border-t">
                                  <td
                                    colSpan="8"
                                    className="px-5 py-5"
                                  >
                                    <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-3">
                                      <DetailCard
                                        icon={
                                          UserRound
                                        }
                                        label="Penandatangan"
                                        value={
                                          signature.signer_common_name ||
                                          '-'
                                        }
                                        subValue={
                                          signature.signer_dn ||
                                          '-'
                                        }
                                      />

                                      <DetailCard
                                        icon={Clock3}
                                        label="Waktu TTE"
                                        value={
                                          signature.signing_time ||
                                          '-'
                                        }
                                        subValue={`Jumlah signature: ${
                                          item.signature_count ||
                                          0
                                        }`}
                                      />

                                      <DetailCard
                                        icon={
                                          Fingerprint
                                        }
                                        label="Algoritma"
                                        value={
                                          signature.hash_algorithm ||
                                          '-'
                                        }
                                        subValue={
                                          signature.signature_type ||
                                          '-'
                                        }
                                      />

                                      <DetailCard
                                        icon={FileKey2}
                                        label="Signature Field"
                                        value={
                                          signature.field_name ||
                                          '-'
                                        }
                                        subValue={
                                          item.whole_document_signed
                                            ? 'Seluruh dokumen tertandatangani'
                                            : 'Perlu cek signed range'
                                        }
                                      />
                                    </div>

                                    <div className="grid lg:grid-cols-2 gap-3 mt-3">
                                      <div className="bg-white border rounded-xl p-4">
                                        <div className="font-bold text-sm">
                                          Validasi Tanda
                                          Tangan
                                        </div>

                                        <div className="mt-2 text-xs text-slate-600 whitespace-pre-wrap break-words leading-relaxed">
                                          {signature.validation ||
                                            '-'}
                                        </div>
                                      </div>

                                      <div className="bg-white border rounded-xl p-4">
                                        <div className="font-bold text-sm">
                                          Validasi
                                          Sertifikat
                                        </div>

                                        <div className="mt-2 text-xs text-slate-600 whitespace-pre-wrap break-words leading-relaxed">
                                          {signature.certificate_validation ||
                                            '-'}
                                        </div>
                                      </div>
                                    </div>

                                    {item.signatures?.length >
                                      1 && (
                                      <div className="mt-4 bg-white border rounded-xl p-4">
                                        <div className="font-bold text-sm mb-3">
                                          Semua Tanda
                                          Tangan (
                                          {
                                            item
                                              .signatures
                                              .length
                                          }
                                          )
                                        </div>

                                        <div className="space-y-3">
                                          {item.signatures.map(
                                            (
                                              sig,
                                              sigIndex
                                            ) => (
                                              <div
                                                key={
                                                  sigIndex
                                                }
                                                className="border rounded-lg p-3 text-xs"
                                              >
                                                <div className="font-bold">
                                                  #
                                                  {sigIndex +
                                                    1}{' '}
                                                  {sig.signer_common_name ||
                                                    '-'}
                                                </div>

                                                <div className="grid md:grid-cols-2 gap-x-5 gap-y-1 mt-2 text-slate-600">
                                                  <div>
                                                    Field:{' '}
                                                    {sig.field_name ||
                                                      '-'}
                                                  </div>
                                                  <div>
                                                    Waktu:{' '}
                                                    {sig.signing_time ||
                                                      '-'}
                                                  </div>
                                                  <div>
                                                    Hash:{' '}
                                                    {sig.hash_algorithm ||
                                                      '-'}
                                                  </div>
                                                  <div>
                                                    Tipe:{' '}
                                                    {sig.signature_type ||
                                                      '-'}
                                                  </div>
                                                </div>
                                              </div>
                                            )
                                          )}
                                        </div>
                                      </div>
                                    )}
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        }
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {result.note && (
              <div className="mt-4 text-xs text-slate-500 bg-white border rounded-xl px-4 py-3">
                {result.note}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function DetailCard({
  icon: Icon,
  label,
  value,
  subValue
}) {
  return (
    <div className="bg-white border rounded-xl p-4 min-w-0">
      <div className="flex items-center gap-2 text-slate-500">
        <Icon size={15} />
        <span className="text-xs font-bold uppercase tracking-wide">
          {label}
        </span>
      </div>

      <div className="font-bold text-sm mt-2 break-words">
        {value}
      </div>

      <div className="text-xs text-slate-500 mt-1 break-words leading-relaxed">
        {subValue}
      </div>
    </div>
  );
}
